import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo } from 'react'
import useConfig from 'modules/config/state/use-config'
import { makeOrderCustomAnswersValidationSchema } from 'modules/order/state/custom-answers/custom-answers-utils'
import { registerForm, unregisterForm } from '../../utils/single-step-form-orchestrator'
import OrderCustomQuestionField from 'modules/order/components/custom-questions/custom-question-field'

interface CustomQuestionsProps {
  showRequired: boolean
}

const CustomQuestions = ({ showRequired }: CustomQuestionsProps) => {
  const config = useConfig()

  const customQuestions = config.questions.custom.filter(
    (question) => question.is_required === showRequired,
  )

  // Create validation schema for filtered questions
  const validationSchema = useMemo(() => {
    const filteredConfig = {
      ...config,
      questions: {
        ...config.questions,
        custom: customQuestions,
      },
    }

    return makeOrderCustomAnswersValidationSchema(filteredConfig)
  }, [config, customQuestions])

  const form = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  // Form name based on required/optional mode
  const formName = showRequired ? 'requiredCustomQuestions' : 'optionalCustomQuestions'

  useEffect(() => {
    registerForm({
      name: formName,
      formRef: form,
      errorMessage: `Please fill in all ${
        showRequired ? 'required' : 'optional'
      } custom question fields`,
    })

    return () => unregisterForm({ name: formName })
  }, [form, formName, showRequired])

  if (customQuestions.length === 0) {
    return null
  }

  return (
    <FormProvider {...form}>
      <form>
        {customQuestions.map((question) => (
          <OrderCustomQuestionField key={question.id} question={question} />
        ))}
      </form>
    </FormProvider>
  )
}

export default CustomQuestions
